import {
  astralyAppUrl,
  astralyLoginUrl,
  astralyLogoutUrl,
} from "@app/lib/compta/astraly_urls";
import { clientFetch } from "@app/lib/egress/client";

// La porte du produit : où part un visiteur SANS session.
//
// Ce module existe pour une raison de frontière, écrite dans l'AGENTS.md
// racine : la décision appartenait à `astraly_urls.ts`, un fichier du premier
// niveau de `lib/compta/` qui est la zone des trois assistants. Plutôt que
// d'écrire chez eux, la règle prescrit d'extraire un module tiers. Celui-ci
// DÉLÈGUE donc à leur fonction pour les cas qui sont les leurs, et n'ajoute
// que la branche du cabinet comptable.
//
// Trois destinations possibles, dans cet ordre :
//
//  1. une app assistante (Pirouette, Electron, Boosts) garde son entrée
//     propre : sa coquille remet la session, l'app n'a pas de mot de passe ;
//  2. le cabinet comptable passe par le login maison de l'app (chantier 19,
//     9.c) quand la bascule est posée au build ;
//  3. sinon le comportement amont de Dust (WorkOS), intact.
//
// Pourquoi un drapeau de build et non une déduction par l'absence du premier
// cas : la bascule doit être RÉVERSIBLE sans déployer de code. L'opérateur
// entre aujourd'hui par la porte du labo ; si le login maison se comporte mal,
// retirer la variable rend la porte d'avant, au lieu de l'enfermer dehors.

/** L'adresse de l'écran de connexion dans le produit. */
export const ROUTE_CONNEXION = "/connexion";

/** Public installation of a new cabinet (série 2, lot C2). */
export const ROUTE_INSTALLATION = "/installation";

/**
 * Le choix d'un nouveau mot de passe, depuis le lien « mot de passe oublié ».
 * Le serveur écrit cette adresse dans son courriel (`password_reset.ts`) : les
 * deux doivent rester d'accord.
 */
export const ROUTE_REINITIALISATION = "/reinitialiser";

/**
 * La bascule, posée au BUILD de la SPA du cabinet.
 *
 * Lu par `process.env` et non par `lib/api/config` [GEN11] : ce code s'exécute
 * dans le navigateur, où le module de configuration serveur n'existe pas. Vite
 * remplace l'expression à la compilation. Même procédé que `astralyAppUrl()`,
 * juste à côté.
 *
 * Comparaison stricte à « true » : un drapeau à moitié posé (« 1 », « oui »)
 * laisse la porte d'avant plutôt que d'en deviner une. Se tromper de porte
 * enferme l'utilisateur dehors, et c'est le seul écran qu'il ne peut pas
 * contourner.
 */
export function porteComptaActive(): boolean {
  return process.env.NEXT_PUBLIC_SELF_HOSTED_LOGIN === "true";
}

export function urlDeLaPorte(
  apiBaseUrl: string,
  returnTo: string | null
): string {
  // Les apps assistantes d'abord : passer la branche comptable devant les
  // priverait de leur propre entrée, et elles n'ont pas de mot de passe.
  if (astralyAppUrl()) {
    return astralyLoginUrl(apiBaseUrl, returnTo);
  }

  if (porteComptaActive()) {
    // La page demandée voyage avec : sans elle, un lien profond reçu par
    // courriel ramène toujours à l'accueil, et l'utilisateur doit se souvenir
    // de ce qu'il voulait voir.
    return returnTo
      ? `${ROUTE_CONNEXION}?retour=${encodeURIComponent(returnTo)}`
      : ROUTE_CONNEXION;
  }

  return astralyLoginUrl(apiBaseUrl, returnTo);
}

/**
 * Where a logout sends the browser. With the self-hosted door, our session is
 * closed first by POST /api/auth/logout: the WorkOS logout does not know our
 * cookie, and an account that "logged out" kept a live session (audit of
 * 15/10/2026). Best effort on the network: a failed call still leads to the
 * login screen, which the server answers with the session's real state.
 */
export async function destinationDeDeconnexion(
  apiBaseUrl: string
): Promise<string> {
  if (astralyAppUrl() || !porteComptaActive()) {
    return astralyLogoutUrl(apiBaseUrl);
  }
  try {
    await clientFetch("/api/auth/logout", { method: "POST" });
  } catch {
    // The network failed: nothing more to do from the browser.
  }
  return ROUTE_CONNEXION;
}

/**
 * L'accueil d'un cabinet dans Linéa : son portefeuille. Null hors de Linéa
 * (une app assistante, ou Dust sans la bascule), où l'accueil reste la
 * conversation de Dust.
 *
 * Décision de Louka du 15/10 : l'écran de conversation de Dust, avec son
 * ancienne barre latérale, n'a pas sa place dans Linéa. L'index du cabinet
 * (`/w/:wId`) et la conversation y renvoient au portefeuille.
 */
export function accueilDuCabinet(workspaceId: string): string | null {
  if (astralyAppUrl() || !porteComptaActive()) {
    return null;
  }
  return `/w/${workspaceId}/dossiers`;
}

/**
 * L'entrée du produit quand rien d'autre n'est demandé : l'index de la SPA
 * mène au portefeuille du cabinet par défaut de l'utilisateur
 * (`?goto=portefeuille`, voir `IndexPage`). Jamais une maquette du labo.
 */
export const ENTREE_PORTEFEUILLE = "/?goto=portefeuille";

/**
 * Où aller une fois le second facteur franchi : la page demandée avant la
 * connexion (`?retour=`, posé par `urlDeLaPorte`), sinon le portefeuille.
 *
 * Seul un chemin de CE site est suivi : « //hote » et « /\hote » mènent
 * ailleurs pour un navigateur, qui efface aussi tabulations et retours à la
 * ligne (« /\t/hote » devient « //hote »). Un lien piégé ferait sinon de la
 * connexion une redirection ouverte vers un faux écran.
 */
export function destinationApresConnexion(retour: string | null): string {
  if (
    retour &&
    retour.startsWith("/") &&
    !retour.startsWith("//") &&
    !/[\u0000-\u001f\u007f\\]/.test(retour)
  ) {
    return retour;
  }
  return ENTREE_PORTEFEUILLE;
}
